import { I18nService } from './i18n.service';

interface ApiValidationFailure {
  PropertyName?: string;
  ErrorMessage?: string;
}

interface ApiValidationError {
  Errors?: ApiValidationFailure[];
  Message?: string;
  message?: string;
}

/** Maps a FluentValidation PropertyName (as returned by the API) to a field-label i18n key. */
const FIELD_LABEL_KEYS: Record<string, string> = {
  AnnouncementFrom: 'ta.announcementFrom',
  AnnouncementTo: 'ta.announcementTo',
  TerminalOperator: 'ta.terminalOperator',
  TerminalOperatorCode: 'ta.terminalOperator',
  WarehouseCode: 'common.warehouse',
  ParkingCargoDescriptionId: 'ta.cargoDescription',
  TrucksQuantity: 'ta.trucksQuantity',
  Description: 'ta.description',
  Note: 'ta.note'
};

/**
 * Turns a raw HttpErrorResponse from the PCS API into a list of readable,
 * bilingual lines. Recognizes the API's FluentValidation shape
 * ({ Errors: [{ PropertyName, ErrorMessage }] }) and known message patterns;
 * falls back to the raw Message text (still one line, not the whole JSON dump)
 * for anything it doesn't recognize, translated where a key matches.
 */
export function parseApiValidationErrors(err: any, i18n: I18nService): string[] {
  const body: ApiValidationError | undefined = err?.error;
  const failures = body?.Errors;

  if (Array.isArray(failures) && failures.length) {
    return failures.map(f => formatFailure(f, i18n));
  }

  const raw = body?.Message ?? body?.message ?? err?.message;
  if (typeof raw === 'string' && raw.trim()) {
    // Strip the generic "Validation errors:\r\n" preamble some responses add;
    // split remaining "Field: message" lines so they don't render as one wall of text.
    const cleaned = raw.replace(/^Validation errors:\s*/i, '').trim();
    return cleaned.split(/\r?\n/).map(line => line.trim()).filter(Boolean).map(line => translateKnownMessage(line, i18n));
  }

  return [];
}

function formatFailure(f: ApiValidationFailure, i18n: I18nService): string {
  const labelKey = f.PropertyName ? FIELD_LABEL_KEYS[f.PropertyName] : undefined;
  const label = labelKey ? i18n.translate(labelKey) : (f.PropertyName ?? '');
  const message = f.ErrorMessage ?? '';

  const known = knownMessage(message, i18n);
  if (known) return label ? `${label}: ${known}` : known;

  return label ? `${label}: ${message}` : message;
}

function translateKnownMessage(line: string, i18n: I18nService): string {
  const idx = line.indexOf(':');
  if (idx === -1) return knownMessage(line, i18n) ?? line;
  const fieldRaw = line.slice(0, idx).trim();
  const messageRaw = line.slice(idx + 1).trim();
  const labelKey = FIELD_LABEL_KEYS[fieldRaw];
  const label = labelKey ? i18n.translate(labelKey) : fieldRaw;
  const known = knownMessage(messageRaw, i18n);
  return `${label}: ${known ?? messageRaw}`;
}

function knownMessage(message: string, i18n: I18nService): string | null {
  const m = message.trim().toLowerCase();
  if (m.includes('should not be empty') || m.includes('is required')) {
    return i18n.translate('validation.required');
  }
  if (m.includes('service requests need to be added to the previous pins')) {
    return i18n.translate('ta.pendingServiceRequestsError');
  }
  return null;
}
